/**
 * Scenarios – LicensingDriverForm
 *
 * Rendered inside DriverForm when the driver type is `licensing_model_change`.
 * The user picks a reusable License Economics record (managed on the Forecast
 * page as scenario inputs), then enters the scenario model + effective timing
 * and optional seat / usage / growth / rate / fixed / minimum-commitment
 * overrides. Produces a ScenarioDriverInput with the licensing fields set.
 */

import { useMemo, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  useListLicenseEconomics,
  getListLicenseEconomicsQueryKey,
} from "@workspace/api-client-react";
import type {
  LicenseModel,
  ScenarioDriverInput,
} from "@workspace/api-client-react";
import { ACTOR } from "./types";
import type { ScenarioScope } from "./types";
import {
  LICENSE_MODEL_LABELS,
  LICENSE_MODEL_ORDER,
  summarizeTiers,
  summarizeTrueUp,
} from "@/components/license-economics/types";

type LForm = {
  licenseEconomicsId: string;
  scenarioModel: LicenseModel | "";
  effectiveFiscalYear: string;
  effectivePeriod: string;
  scenarioSeats: string;
  seatDelta: string;
  seatPercentage: string;
  scenarioUsage: string;
  usageGrowthPercentage: string;
  seatRateOverride: string;
  unitRateOverride: string;
  fixedMonthlyOverride: string;
  minimumMonthlyCommitmentOverride: string;
  optimizationType: "" | "reclaim_unused";
  reclaimSeats: string;
  timingBasis: "explicit" | "renewal";
};

const emptyForm: LForm = {
  licenseEconomicsId: "",
  scenarioModel: "",
  effectiveFiscalYear: "",
  effectivePeriod: "",
  scenarioSeats: "",
  seatDelta: "",
  seatPercentage: "",
  scenarioUsage: "",
  usageGrowthPercentage: "",
  seatRateOverride: "",
  unitRateOverride: "",
  fixedMonthlyOverride: "",
  minimumMonthlyCommitmentOverride: "",
  optimizationType: "",
  reclaimSeats: "",
  timingBasis: "explicit",
};

function numOrUndef(value: string): number | undefined {
  if (value.trim() === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function LicensingDriverForm({
  name,
  scope,
  onSubmit,
  onCancel,
  isPending,
  error,
}: {
  name: string;
  scope: ScenarioScope;
  onSubmit: (driver: ScenarioDriverInput) => void;
  onCancel: () => void;
  isPending: boolean;
  error?: string;
}) {
  const [form, setForm] = useState<LForm>(emptyForm);

  const { data: records, isLoading } = useListLicenseEconomics(scope, {
    query: { queryKey: getListLicenseEconomicsQueryKey(scope) },
  });

  const activeRecords = useMemo(
    () => (records ?? []).filter((r) => r.status !== "retired"),
    [records]
  );

  const selected = activeRecords.find(
    (r) => r.licenseEconomicsId === form.licenseEconomicsId
  );

  const set = <K extends keyof LForm>(key: K, value: LForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const canSubmit = name.trim().length > 0 && !!form.licenseEconomicsId;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const driver: ScenarioDriverInput = {
      questionKey: "test_license_economics",
      type: "licensing_model_change",
      name: name.trim(),
      actor: ACTOR,
      licenseEconomicsId: form.licenseEconomicsId,
      ...(form.optimizationType ? { optimizationType: form.optimizationType } : {}),
      ...(form.optimizationType && numOrUndef(form.reclaimSeats) !== undefined ? { reclaimSeats: numOrUndef(form.reclaimSeats) } : {}),
      timingBasis: form.timingBasis,
      ...(form.scenarioModel ? { scenarioModel: form.scenarioModel } : {}),
      ...(numOrUndef(form.effectiveFiscalYear) !== undefined
        ? { effectiveFiscalYear: numOrUndef(form.effectiveFiscalYear) }
        : {}),
      ...(numOrUndef(form.effectivePeriod) !== undefined
        ? { effectivePeriod: numOrUndef(form.effectivePeriod) }
        : {}),
      ...(numOrUndef(form.scenarioSeats) !== undefined
        ? { scenarioSeats: numOrUndef(form.scenarioSeats) }
        : {}),
      ...(numOrUndef(form.seatDelta) !== undefined
        ? { seatDelta: numOrUndef(form.seatDelta) }
        : {}),
      ...(numOrUndef(form.seatPercentage) !== undefined
        ? { seatPercentage: numOrUndef(form.seatPercentage) }
        : {}),
      ...(numOrUndef(form.scenarioUsage) !== undefined
        ? { scenarioUsage: numOrUndef(form.scenarioUsage) }
        : {}),
      ...(numOrUndef(form.usageGrowthPercentage) !== undefined
        ? { usageGrowthPercentage: numOrUndef(form.usageGrowthPercentage) }
        : {}),
      ...(numOrUndef(form.seatRateOverride) !== undefined
        ? { seatRateOverride: numOrUndef(form.seatRateOverride) }
        : {}),
      ...(numOrUndef(form.unitRateOverride) !== undefined
        ? { unitRateOverride: numOrUndef(form.unitRateOverride) }
        : {}),
      ...(numOrUndef(form.fixedMonthlyOverride) !== undefined
        ? { fixedMonthlyOverride: numOrUndef(form.fixedMonthlyOverride) }
        : {}),
      ...(numOrUndef(form.minimumMonthlyCommitmentOverride) !== undefined
        ? {
            minimumMonthlyCommitmentOverride: numOrUndef(
              form.minimumMonthlyCommitmentOverride
            ),
          }
        : {}),
    };
    onSubmit(driver);
  };

  return (
    <div className="sc-le-driver" data-testid="licensing-driver-fields">
      {/* Reusable input selection */}
      <div className="sc-le-section-label">Reusable license economics input</div>

      {isLoading && (
        <div className="le-loading" style={{ padding: 12 }}>
          <Loader2 size={14} className="animate-spin" /> Loading records…
        </div>
      )}

      {!isLoading && activeRecords.length === 0 && (
        <div className="sc-form-error" role="alert">
          <AlertCircle size={13} />
          <span>
            No active License Economics records for FY{scope.fiscalYear} · dept{" "}
            {scope.departmentCode}. Create one on the Forecast page under
            Scenario Inputs first.
          </span>
        </div>
      )}

      {!isLoading && activeRecords.length > 0 && (
        <div className="sc-field">
          <label className="sc-label">
            License record <span className="sc-required">*</span>
          </label>
          <select
            value={form.licenseEconomicsId}
            onChange={(e) => set("licenseEconomicsId", e.target.value)}
            data-testid="select-le-record"
          >
            <option value="">Select a license economics record…</option>
            {activeRecords.map((r) => (
              <option key={r.licenseEconomicsId} value={r.licenseEconomicsId}>
                {r.vendor} — {r.productWorkload} ({r.glAccount})
              </option>
            ))}
          </select>
        </div>
      )}

      {selected && (
        <div className="sc-le-select-card" style={{ marginTop: 8 }}>
          <strong>
            {selected.vendor} · {selected.productWorkload}
          </strong>
          <span>
            Current model: {LICENSE_MODEL_LABELS[selected.currentModel]} · Seats{" "}
            {selected.activeSeats}/{selected.seats} · Min commit ${selected.minimumMonthlyCommitment}
          </span>
          <span>
            Seat pricing: {summarizeTiers(selected.seatTiers, selected.seatRate, "seat")}
          </span>
          <span>
            Usage pricing: {summarizeTiers(selected.usageTiers, selected.unitRate, selected.usageUnit || "unit")}
          </span>
          <span>True-up: {summarizeTrueUp(selected)}</span>
          <span className="sc-le-capture-note">
            These contract terms are captured as an immutable snapshot when the driver is added.
          </span>
        </div>
      )}

      <div className="sc-le-section-label">Unused-license optimization</div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">Assumption</label>
          <select value={form.optimizationType} onChange={(e) => set("optimizationType", e.target.value as LForm["optimizationType"])} data-testid="select-license-optimization">
            <option value="">Model change only</option>
            <option value="reclaim_unused">Explicitly reclaim unused seats</option>
          </select>
        </div>
        {form.optimizationType === "reclaim_unused" && (
          <div className="sc-field">
            <label className="sc-label">Seats to reclaim</label>
            <input type="number" min={0} max={selected ? Math.max(0, selected.seats - selected.activeSeats) : undefined}
              value={form.reclaimSeats} onChange={(e) => set("reclaimSeats", e.target.value)}
              placeholder={selected ? `Up to ${Math.max(0, selected.seats - selected.activeSeats)}` : "Unused seats"}
              data-testid="input-reclaim-seats" />
          </div>
        )}
        <div className="sc-field">
          <label className="sc-label">Savings timing</label>
          <select value={form.timingBasis} onChange={(e) => set("timingBasis", e.target.value as LForm["timingBasis"])} data-testid="select-license-timing">
            <option value="explicit">Explicit forecast period</option>
            <option value="renewal">At contract renewal (map below)</option>
          </select>
        </div>
      </div>
      {form.timingBasis === "renewal" && (
        <div className="sc-le-select-card">
          <strong>Map the contract renewal to the accounting calendar</strong>
          <span>Enter the renewal fiscal year and period below. TekLedger does not assume calendar months equal fiscal periods.</span>
        </div>
      )}
      {form.optimizationType === "reclaim_unused" && selected && (
        <div className="sc-le-select-card">
          <strong>{Math.max(0, selected.seats - selected.activeSeats)} unused seats available</strong>
          <span>Active seats remain utilization evidence. The billable seat assumption changes only by the explicit reclaim entered here, subject to the monthly minimum.</span>
        </div>
      )}

      {/* Scenario model + timing */}
      <div className="sc-le-section-label">Scenario model &amp; timing</div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">Scenario model</label>
          <select
            value={form.scenarioModel}
            onChange={(e) =>
              set("scenarioModel", e.target.value as LicenseModel | "")
            }
            data-testid="select-scenario-model"
          >
            <option value="">
              {selected
                ? `Use record future model (${LICENSE_MODEL_LABELS[selected.futureModel]})`
                : "Use record future model"}
            </option>
            {LICENSE_MODEL_ORDER.map((m) => (
              <option key={m} value={m}>
                {LICENSE_MODEL_LABELS[m]}
              </option>
            ))}
          </select>
        </div>
        <div className="sc-field">
          <label className="sc-label">Effective fiscal year</label>
          <input
            type="number"
            min={1900}
            max={9999}
            value={form.effectiveFiscalYear}
            onChange={(e) => set("effectiveFiscalYear", e.target.value)}
            placeholder={`Default FY${scope.fiscalYear}`}
            data-testid="input-le-eff-fy"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Effective period (1–12)</label>
          <input
            type="number"
            min={1}
            max={12}
            value={form.effectivePeriod}
            onChange={(e) => set("effectivePeriod", e.target.value)}
            placeholder="Default: full year"
            data-testid="input-le-eff-period"
          />
        </div>
      </div>

      {/* Seat overrides */}
      <div className="sc-le-section-label">Seat overrides (optional)</div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">Scenario seats</label>
          <input
            type="number"
            min={0}
            value={form.scenarioSeats}
            onChange={(e) => set("scenarioSeats", e.target.value)}
            placeholder="Absolute seat count"
            data-testid="input-scenario-seats"
            disabled={form.optimizationType === "reclaim_unused"}
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Seat delta</label>
          <input
            type="number"
            value={form.seatDelta}
            onChange={(e) => set("seatDelta", e.target.value)}
            placeholder="Signed change (+/-)"
            data-testid="input-seat-delta"
            disabled={form.optimizationType === "reclaim_unused"}
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Seat change (%)</label>
          <input
            type="number"
            value={form.seatPercentage}
            onChange={(e) => set("seatPercentage", e.target.value)}
            placeholder="e.g. 10 for +10%"
            data-testid="input-seat-percentage"
            disabled={form.optimizationType === "reclaim_unused"}
          />
        </div>
      </div>

      {/* Usage overrides */}
      <div className="sc-le-section-label">Usage overrides (optional)</div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">Scenario monthly usage</label>
          <input
            type="number"
            min={0}
            value={form.scenarioUsage}
            onChange={(e) => set("scenarioUsage", e.target.value)}
            placeholder="Absolute usage"
            data-testid="input-scenario-usage"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Usage growth (%)</label>
          <input
            type="number"
            value={form.usageGrowthPercentage}
            onChange={(e) => set("usageGrowthPercentage", e.target.value)}
            placeholder="e.g. 15 for +15%"
            data-testid="input-usage-growth"
          />
        </div>
      </div>

      {/* Rate + fixed overrides */}
      <div className="sc-le-section-label">Rate, fixed &amp; floor overrides (optional)</div>
      <div className="note" style={{ marginBottom: 8 }}>
        Scalar rate overrides replace the captured tier schedule for that rate.
        Leave blank to use the immutable tiered terms shown above.
      </div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">Seat rate override ($)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.seatRateOverride}
            onChange={(e) => set("seatRateOverride", e.target.value)}
            data-testid="input-seat-rate-override"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Unit rate override ($)</label>
          <input
            type="number"
            min={0}
            step="0.0001"
            value={form.unitRateOverride}
            onChange={(e) => set("unitRateOverride", e.target.value)}
            data-testid="input-unit-rate-override"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Fixed monthly override ($)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.fixedMonthlyOverride}
            onChange={(e) => set("fixedMonthlyOverride", e.target.value)}
            data-testid="input-fixed-override"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Minimum commitment override ($)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.minimumMonthlyCommitmentOverride}
            onChange={(e) =>
              set("minimumMonthlyCommitmentOverride", e.target.value)
            }
            data-testid="input-minimum-override"
          />
        </div>
      </div>

      {error && (
        <div
          className="sc-form-error"
          role="alert"
          style={{ marginTop: 14 }}
          data-testid="licensing-driver-error"
        >
          <AlertCircle size={13} />
          <span>{error}</span>
        </div>
      )}

      <div className="sc-driver-form__actions" style={{ marginTop: 14 }}>
        <button
          type="button"
          className="tl-btn"
          onClick={onCancel}
          data-testid="button-cancel-licensing-driver"
        >
          Cancel
        </button>
        <button
          type="button"
          className="tl-btn primary"
          onClick={handleSubmit}
          disabled={isPending || !canSubmit}
          data-testid="button-submit-licensing-driver"
        >
          {isPending ? (
            <>
              <Loader2 size={12} className="animate-spin" /> Saving driver…
            </>
          ) : (
            "Add licensing driver"
          )}
        </button>
      </div>
    </div>
  );
}
